import { useEffect, useState } from "react";
import { api, fmtEur, formatErr } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import {
  Users, UserCircle, Buildings, MagnifyingGlass, Plus, Trash, PencilSimple,
  Receipt, Calendar as CalendarIcon, Phone, Envelope, IdentificationCard, MapPinLine, X,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { EmptyState } from "../components/EmptyState";

const EMPTY = {
  type: "particular", name: "", razon_social: "", nif_cif: "",
  phone: "", email: "", address_fiscal: "", main_license_plate: "", notes: "",
  is_public_org: false, dir3_oficina_contable: "", dir3_organo_gestor: "", dir3_unidad_tramitadora: "",
};

function CustomerForm({ value, onChange }) {
  const v = value;
  const set = (k) => (e) => onChange({ ...v, [k]: e.target?.value ?? e });
  const isEmpresa = v.type === "empresa";
  return (
    <div className="space-y-4">
      <div>
        <Label className="text-xs uppercase tracking-wider">Tipo de cliente *</Label>
        <div className="mt-1 grid grid-cols-2 gap-2 p-1 bg-[var(--ag-midnight)] rounded border border-[var(--ag-border)]">
          <button type="button" onClick={()=>onChange({...v, type:"particular"})}
            className={`px-3 py-2 text-xs uppercase tracking-wider rounded flex items-center justify-center gap-1.5 transition-colors ${v.type==="particular"?"bg-[var(--ag-aqua)] text-white":"text-[var(--ag-text-dim)] hover:text-white"}`}
            data-testid="cust-type-particular">
            <UserCircle size={13} weight="fill" /> Particular
          </button>
          <button type="button" onClick={()=>onChange({...v, type:"empresa"})}
            className={`px-3 py-2 text-xs uppercase tracking-wider rounded flex items-center justify-center gap-1.5 transition-colors ${v.type==="empresa"?"bg-[var(--ag-gold)] text-[var(--ag-midnight)]":"text-[var(--ag-text-dim)] hover:text-white"}`}
            data-testid="cust-type-empresa">
            <Buildings size={13} weight="fill" /> Empresa
          </button>
        </div>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wider">{isEmpresa ? "Nombre comercial *" : "Nombre completo *"}</Label>
        <Input value={v.name} onChange={set("name")} className="mt-1" data-testid="cust-name" />
      </div>
      {isEmpresa && (
        <>
          <div>
            <Label className="text-xs uppercase tracking-wider">Razón social *</Label>
            <Input value={v.razon_social} onChange={set("razon_social")} className="mt-1" placeholder="Ej. Logistic Sevilla SL" data-testid="cust-razon" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">CIF *</Label>
            <Input value={v.nif_cif} onChange={set("nif_cif")} className="mt-1 font-mono-tab uppercase" placeholder="B12345678" data-testid="cust-cif" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Dirección fiscal *</Label>
            <Input value={v.address_fiscal} onChange={set("address_fiscal")} className="mt-1" placeholder="Av. Comercio 45, 41005 Sevilla" data-testid="cust-address" />
          </div>
          {/* DIR3 — Organismo Público / Flota Oficial */}
          <label className="flex items-start gap-2 p-3 border border-amber-400/30 bg-amber-500/5 rounded-sm cursor-pointer" data-testid="cust-public-toggle">
            <input
              type="checkbox"
              checked={!!v.is_public_org}
              onChange={(e)=>onChange({...v, is_public_org: e.target.checked})}
              className="h-4 w-4 mt-0.5 accent-amber-500"
            />
            <div className="text-sm">
              <div className="font-medium">Es un <b>Organismo Público / Flota Oficial</b></div>
              <div className="text-[11px] text-[var(--ag-text-dim)] mt-0.5 normal-case">Activa los códigos DIR3 obligatorios para Facturae (Ayuntamientos, Diputaciones, Ministerios, flotas oficiales…)</div>
            </div>
          </label>
          {v.is_public_org && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-3 border border-[var(--ag-border)] rounded-sm bg-[var(--ag-midnight)]" data-testid="cust-dir3-block">
              <div>
                <Label className="text-xs uppercase tracking-wider">Oficina Contable *</Label>
                <Input value={v.dir3_oficina_contable} onChange={(e)=>onChange({...v, dir3_oficina_contable: e.target.value.toUpperCase()})}
                  className="mt-1 font-mono-tab" placeholder="L01280796" data-testid="cust-dir3-oc" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Órgano Gestor *</Label>
                <Input value={v.dir3_organo_gestor} onChange={(e)=>onChange({...v, dir3_organo_gestor: e.target.value.toUpperCase()})}
                  className="mt-1 font-mono-tab" placeholder="LA0001329" data-testid="cust-dir3-og" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Unidad Tramitadora *</Label>
                <Input value={v.dir3_unidad_tramitadora} onChange={(e)=>onChange({...v, dir3_unidad_tramitadora: e.target.value.toUpperCase()})}
                  className="mt-1 font-mono-tab" placeholder="LA0001407" data-testid="cust-dir3-ut" />
              </div>
              <p className="md:col-span-3 text-[11px] text-[var(--ag-text-dim)]">Estos códigos aparecerán en la factura PDF y prepararán la exportación al XML Facturae para FACe/FACeB2B.</p>
            </div>
          )}
        </>
      )}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs uppercase tracking-wider">Email</Label>
          <Input type="email" value={v.email} onChange={set("email")} className="mt-1" data-testid="cust-email" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider">Teléfono</Label>
          <Input value={v.phone} onChange={set("phone")} className="mt-1" placeholder="+34..." data-testid="cust-phone" />
        </div>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wider">Matrícula principal</Label>
        <Input value={v.main_license_plate} onChange={(e)=>onChange({...v, main_license_plate: e.target.value.toUpperCase()})} className="mt-1 font-mono-tab uppercase" placeholder="1234ABC" data-testid="cust-plate" />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wider">Notas internas</Label>
        <Input value={v.notes} onChange={set("notes")} className="mt-1" placeholder="Cliente VIP · descuento aplicado..." data-testid="cust-notes" />
      </div>
    </div>
  );
}

export default function Customers() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [filterType, setFilterType] = useState(""); // ""|"particular"|"empresa"
  const [loading, setLoading] = useState(true);
  const [openNew, setOpenNew] = useState(false);
  const [newForm, setNewForm] = useState(EMPTY);
  const [detail, setDetail] = useState(null); // full customer object
  const [busy, setBusy] = useState(false);

  const load = async (qVal = "", typeVal = "") => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (qVal) p.set("q", qVal);
      if (typeVal) p.set("type", typeVal);
      const { data } = await api.get(`/customers?${p.toString()}`);
      setItems(data);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load("", ""); }, []);

  const openDetail = async (id) => {
    try {
      const { data } = await api.get(`/customers/${id}`);
      setDetail(data);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const create = async () => {
    if (!newForm.name.trim()) { toast.error("Nombre obligatorio"); return; }
    if (newForm.type === "empresa" && (!newForm.razon_social.trim() || !newForm.nif_cif.trim())) {
      toast.error("Razón social y CIF obligatorios para empresas"); return;
    }
    setBusy(true);
    try {
      await api.post("/customers", newForm);
      toast.success("Cliente creado");
      setOpenNew(false); setNewForm(EMPTY);
      load(q, filterType);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const del = async (c) => {
    if (!window.confirm(`¿Eliminar a ${c.name}?`)) return;
    try {
      const { data } = await api.delete(`/customers/${c.id}`);
      toast.success(data.archived ? "Cliente archivado (tiene historial)" : "Cliente eliminado");
      load(q, filterType);
      if (detail?.id === c.id) setDetail(null);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const particulares = items.filter((c) => c.type === "particular").length;
  const empresas = items.filter((c) => c.type === "empresa").length;

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[var(--ag-text-dim)]">CRM</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tighter mt-1 text-[var(--ag-text)]">Clientes</h1>
          <p className="text-sm text-[var(--ag-text-dim)] mt-2">
            <Users size={13} weight="duotone" className="inline mr-1" /> {items.length} total · {particulares} particular{particulares===1?"":"es"} · {empresas} empresa{empresas===1?"":"s"}
          </p>
        </div>
        <Button onClick={()=>{ setNewForm(EMPTY); setOpenNew(true); }} className="ag-btn-gold rounded-sm" data-testid="new-customer-btn">
          <Plus className="mr-1.5" weight="bold" /> Nuevo cliente
        </Button>
      </header>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <form onSubmit={(e)=>{e.preventDefault(); load(q, filterType);}} className="flex items-center gap-2 flex-1 min-w-[260px]">
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ag-text-dim)]" />
            <Input value={q} onChange={(e)=>setQ(e.target.value)} className="pl-9 rounded-sm h-10" placeholder="Buscar por nombre, NIF, email, matrícula…" data-testid="cust-search" />
          </div>
          <Button type="submit" variant="outline" className="rounded-sm border-[var(--ag-border)] bg-transparent text-[var(--ag-text)] hover:bg-[var(--ag-midnight-soft)]">Buscar</Button>
        </form>
        <div className="flex items-center gap-1 p-1 bg-[var(--ag-midnight-soft)] rounded border border-[var(--ag-border)]">
          {[
            { v: "", label: "Todos" },
            { v: "particular", label: "Particulares" },
            { v: "empresa", label: "Empresas" },
          ].map((opt) => (
            <button key={opt.v} onClick={()=>{ setFilterType(opt.v); load(q, opt.v); }}
              className={`px-3 py-1.5 text-xs rounded transition-colors ${filterType===opt.v?"bg-[var(--ag-gold)] text-[var(--ag-midnight)] font-semibold":"text-[var(--ag-text-dim)] hover:text-white"}`}
              data-testid={`cust-filter-${opt.v||"all"}`}>
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      <div className="ag-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--ag-border)] bg-[var(--ag-midnight-soft)]">
                <th className="text-left text-[10px] uppercase tracking-[0.15em] text-[var(--ag-text-dim)] px-4 py-3">Cliente</th>
                <th className="text-left text-[10px] uppercase tracking-[0.15em] text-[var(--ag-text-dim)] px-4 py-3">Tipo</th>
                <th className="text-left text-[10px] uppercase tracking-[0.15em] text-[var(--ag-text-dim)] px-4 py-3">NIF/CIF</th>
                <th className="text-left text-[10px] uppercase tracking-[0.15em] text-[var(--ag-text-dim)] px-4 py-3">Matrícula</th>
                <th className="text-left text-[10px] uppercase tracking-[0.15em] text-[var(--ag-text-dim)] px-4 py-3">Contacto</th>
                <th className="text-center text-[10px] uppercase tracking-[0.15em] text-[var(--ag-text-dim)] px-4 py-3">Servicios</th>
                <th className="text-right text-[10px] uppercase tracking-[0.15em] text-[var(--ag-text-dim)] px-4 py-3">Gastado</th>
                <th className="text-right text-[10px] uppercase tracking-[0.15em] text-[var(--ag-text-dim)] px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading && ["a","b","c"].map((k)=><tr key={`sk-${k}`}><td colSpan={8} className="px-4 py-4"><div className="h-5 bg-[var(--ag-midnight-soft)] rounded animate-pulse" /></td></tr>)}
              {!loading && items.length === 0 && (
                <tr><td colSpan={8} className="py-6">
                  <EmptyState
                    icon={Users}
                    title="Aún no tienes clientes"
                    description="Los clientes se crean automáticamente cuando alguien reserva online. También puedes añadirlos manualmente."
                    primaryCta={{ onClick: () => setOpenNew(true), label: "Añadir cliente" }}
                    secondaryCta={{ to: "/marketplace", label: "Ver mi perfil público" }}
                    tip="Tip: comparte tu enlace público para empezar a recibir clientes automáticamente."
                    testId="customers-empty"
                  />
                </td></tr>
              )}
              {items.map((c) => (
                <tr key={c.id} onClick={()=>openDetail(c.id)} className="border-b border-[var(--ag-border)] hover:bg-[var(--ag-midnight-soft)]/40 cursor-pointer transition-colors" data-testid={`cust-row-${c.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-[var(--ag-text)]">{c.type === "empresa" ? (c.razon_social || c.name) : c.name}</div>
                    {c.type === "empresa" && c.razon_social && c.name && c.name !== c.razon_social && (
                      <div className="text-[11px] text-[var(--ag-text-dim)]">{c.name}</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {c.type === "empresa" ? (
                      <Badge className="bg-[var(--ag-gold)] text-[var(--ag-midnight)] hover:bg-[var(--ag-gold)] text-[10px] uppercase tracking-wider"><Buildings size={11} weight="fill" className="mr-1" /> Empresa</Badge>
                    ) : (
                      <Badge className="bg-[var(--ag-aqua)]/20 text-[var(--ag-aqua-soft)] hover:bg-[var(--ag-aqua)]/20 border border-[var(--ag-aqua)]/40 text-[10px] uppercase tracking-wider"><UserCircle size={11} weight="fill" className="mr-1" /> Particular</Badge>
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono-tab text-[var(--ag-text-dim)]">{c.nif_cif || "—"}</td>
                  <td className="px-4 py-3 font-mono-tab">{c.main_license_plate || "—"}</td>
                  <td className="px-4 py-3 text-[var(--ag-text-dim)] text-xs">
                    {c.email && <div className="flex items-center gap-1"><Envelope size={11} /> {c.email}</div>}
                    {c.phone && <div className="flex items-center gap-1"><Phone size={11} /> {c.phone}</div>}
                  </td>
                  <td className="px-4 py-3 text-center tabular-nums">{c.service_count}</td>
                  <td className="px-4 py-3 text-right tabular-nums font-medium">{fmtEur(c.total_spent || 0)}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <Button size="icon" variant="ghost" onClick={(e)=>{ e.stopPropagation(); del(c); }} className="h-8 w-8 hover:bg-red-500/10" data-testid={`cust-del-${c.id}`}>
                      <Trash size={13} className="text-red-400" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create dialog */}
      <Dialog open={openNew} onOpenChange={setOpenNew}>
        <DialogContent className="rounded-sm max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tighter">Nuevo cliente</DialogTitle>
            <DialogDescription className="text-xs text-[var(--ag-text-dim)]">
              Selecciona Particular (ticket simplificado) o Empresa (factura completa con CIF y dirección fiscal).
            </DialogDescription>
          </DialogHeader>
          <CustomerForm value={newForm} onChange={setNewForm} />
          <DialogFooter className="mt-2">
            <Button variant="outline" onClick={()=>setOpenNew(false)} className="rounded-sm">Cancelar</Button>
            <Button onClick={create} disabled={busy} className="ag-btn-gold rounded-sm" data-testid="cust-submit">{busy ? "Guardando…" : "Crear cliente"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(v)=>!v && setDetail(null)}>
        <DialogContent className="rounded-sm max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="cust-detail">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl tracking-tighter flex items-center gap-2">
                  {detail.type === "empresa" ? <Buildings weight="fill" className="text-[var(--ag-gold)]" /> : <UserCircle weight="fill" className="text-[var(--ag-aqua-soft)]" />}
                  {detail.type === "empresa" ? (detail.razon_social || detail.name) : detail.name}
                </DialogTitle>
                <DialogDescription className="text-xs text-[var(--ag-text-dim)]">
                  Ficha completa del cliente con historial de servicios y reservas.
                </DialogDescription>
              </DialogHeader>
              <Tabs defaultValue="info">
                <TabsList>
                  <TabsTrigger value="info">Datos</TabsTrigger>
                  <TabsTrigger value="history">Historial ({detail.service_count})</TabsTrigger>
                  <TabsTrigger value="appts">Reservas ({detail.appointment_count})</TabsTrigger>
                </TabsList>
                <TabsContent value="info" className="pt-4 space-y-2 text-sm">
                  {detail.type === "empresa" ? (
                    <Badge className="bg-[var(--ag-gold)] text-[var(--ag-midnight)] hover:bg-[var(--ag-gold)]"><Buildings size={11} className="mr-1" weight="fill" /> Empresa · facturación completa</Badge>
                  ) : (
                    <Badge className="bg-[var(--ag-aqua)]/20 text-[var(--ag-aqua-soft)] border border-[var(--ag-aqua)]/40"><UserCircle size={11} className="mr-1" weight="fill" /> Particular · ticket simplificado</Badge>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                    {detail.nif_cif && <DetailRow icon={IdentificationCard} label="NIF/CIF" value={detail.nif_cif} />}
                    {detail.main_license_plate && <DetailRow icon={Receipt} label="Matrícula" value={detail.main_license_plate} />}
                    {detail.email && <DetailRow icon={Envelope} label="Email" value={detail.email} />}
                    {detail.phone && <DetailRow icon={Phone} label="Teléfono" value={detail.phone} />}
                    {detail.address_fiscal && <DetailRow icon={MapPinLine} label="Dirección fiscal" value={detail.address_fiscal} full />}
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-4 p-3 bg-[var(--ag-midnight-soft)] rounded">
                    <div className="text-center">
                      <div className="text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)]">Total servicios</div>
                      <div className="font-display font-bold text-2xl tabular-nums">{detail.service_count}</div>
                    </div>
                    <div className="text-center">
                      <div className="text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)]">Total gastado</div>
                      <div className="font-display font-bold text-2xl tabular-nums text-[var(--ag-gold)]">{fmtEur(detail.total_spent)}</div>
                    </div>
                  </div>
                  {detail.notes && <div className="text-xs text-[var(--ag-text-dim)] mt-3 italic">📝 {detail.notes}</div>}
                </TabsContent>
                <TabsContent value="history" className="pt-4">
                  {detail.services.length === 0 ? (
                    <div className="text-center text-[var(--ag-text-dim)] py-8 text-sm">Sin servicios registrados.</div>
                  ) : (
                    <div className="space-y-1.5">
                      {detail.services.map((s) => (
                        <div key={s.id} className="flex items-center gap-2 p-2.5 bg-[var(--ag-midnight-soft)] rounded text-xs">
                          <Receipt size={14} className="text-[var(--ag-gold)] shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium truncate">{s.wash_type}</div>
                            <div className="text-[10px] text-[var(--ag-text-dim)] tabular-nums">{s.date.slice(0, 10)} · {s.invoice_number}</div>
                          </div>
                          <div className="font-medium tabular-nums">{fmtEur(s.total_price)}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>
                <TabsContent value="appts" className="pt-4">
                  {detail.appointments.length === 0 ? (
                    <div className="text-center text-[var(--ag-text-dim)] py-8 text-sm">Sin reservas registradas.</div>
                  ) : (
                    <div className="space-y-1.5">
                      {detail.appointments.map((a) => (
                        <div key={a.id} className="flex items-center gap-2 p-2.5 bg-[var(--ag-midnight-soft)] rounded text-xs">
                          <CalendarIcon size={14} className="text-[var(--ag-aqua-soft)] shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium truncate">{a.wash_type} <Badge variant="outline" className="ml-1 text-[9px] border-[var(--ag-border)]">{a.status}</Badge></div>
                            <div className="text-[10px] text-[var(--ag-text-dim)] tabular-nums">{new Date(a.date).toLocaleString("es-ES")}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DetailRow({ icon: Icon, label, value, full }) {
  return (
    <div className={`flex items-start gap-2 p-2.5 bg-[var(--ag-midnight-soft)] rounded ${full ? "sm:col-span-2" : ""}`}>
      <Icon size={14} className="text-[var(--ag-text-dim)] shrink-0 mt-0.5" />
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)]">{label}</div>
        <div className="text-sm text-[var(--ag-text)] break-words">{value}</div>
      </div>
    </div>
  );
}
